// src/hooks/useTheme.js
// Light / dark theme for the app shell and themed pages.
// The choice is saved per browser and applied as data-theme on <html>.
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'clockout-theme';
const EVENT = 'clockout-theme-change';

function readTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* storage blocked — fall through */ }
  return 'light';
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export function useTheme() {
  const [theme, setThemeState] = useState(readTheme);

  // Keep <html> in sync, and keep every component using the hook in sync
  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => {
    const onChange = e => setThemeState(e.detail);
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  const setTheme = useCallback(next => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    applyTheme(next);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  }, []);

  return { theme, setTheme, toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark') };
}